/* DocSheet (DESIGN24 §T2) — the atoms BOTH mini-app document tabs share:

     • the APPROVAL affordance (DESIGN23 stage 1) — «Approved 1 of 2», the
       quiet «You approved · <time>» state and the muted «Waiting for the
       assigned approvers» line, rendered in Telegram-native chrome;
     • the AI CHAT bottom sheet — a thread with an Edit / Ask mode toggle, a
       model picker, suggestion chips, a growing composer and an optional
       «Clear» action.

   THE APPROVAL RULES ARE NOT RESTATED HERE. T1's tmaApi.ts owns them for the
   whole mini app (`approvalLines` / `canApproveNow` / `myApprovalTime` /
   `useApprovalStatus`), which is where the architecture step reads them too —
   so all three steps speak one dialect and there is exactly one place the
   «no policy ⇒ behave exactly as before stage 1» rule lives. This module only
   turns those plain strings into markup, and adds the ONE thing the document
   tabs need beyond a single target: the same read for a LIST of them (the
   estimate / proposal rows).

   Mobile has no hover, so the web's tooltip becomes an always-visible list of
   the votes underneath the counter. English throughout; styles in
   tma-docs.css. */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { X } from 'lucide-react';
import {
  approvalsApi,
  ApprovalStatus,
  ApprovalStep,
  ApprovalTargetKind,
} from '../../architect/api';
import { autoGrow, fmtDate, fmtK } from '../../architect/utils';
import { approvalLines, canApproveNow, myApprovalTime } from '../tmaApi';
import { haptic } from '../telegram';
import { Sheet } from './Sheet';
import '../tma-docs.css';

/* ═══════════════ approvals ═══════════════════════════════════ */

// Re-exported so a document tab has ONE import for the whole approval
// affordance; the implementations stay T1's (src/tma/tmaApi.ts).
export { approvalLines, canApproveNow, myApprovalTime };

/** Live approval status for ONE target, with an explicit refresh key so a
    just-cast vote (or a spec edit, which VOIDS the votes) re-reads at once.
    Silent on failure by design: no status means «no policy», which is exactly
    the pre-stage-1 behaviour — never a hidden button. */
export function useApprovalStatus(
  step: ApprovalStep,
  targetKind: ApprovalTargetKind,
  targetId: string | null | undefined,
  enabled: boolean,
  refreshKey: number,
): ApprovalStatus | null {
  const [status, setStatus] = useState<ApprovalStatus | null>(null);
  useEffect(() => {
    if (!enabled || !targetId) {
      setStatus(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const st = await approvalsApi.getStatus({
          step,
          target_kind: targetKind,
          target_id: targetId,
        });
        if (!cancelled) setStatus(st);
      } catch {
        /* keep whatever we had — never a banner, never a hidden button */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, targetKind, targetId, enabled, refreshKey]);
  return status;
}

/** The same for a LIST of targets (the estimate / proposal rows). Reads run
    in parallel and each fails independently, so one bad row can never blank
    the others. `ids` is joined into the effect key so a stable list does not
    refetch on every render. */
export function useApprovalStatuses(
  step: ApprovalStep,
  targetKind: ApprovalTargetKind,
  ids: string[],
  enabled: boolean,
  refreshKey: number,
): Record<string, ApprovalStatus> {
  const [map, setMap] = useState<Record<string, ApprovalStatus>>({});
  const key = ids.join(',');
  useEffect(() => {
    if (!enabled || !key) {
      setMap({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const out: Record<string, ApprovalStatus> = {};
      await Promise.all(
        key.split(',').map(async (id) => {
          try {
            out[id] = await approvalsApi.getStatus({
              step,
              target_kind: targetKind,
              target_id: id,
            });
          } catch {
            /* this row simply has no status → today's behaviour */
          }
        }),
      );
      if (!cancelled) setMap(out);
    })();
    return () => {
      cancelled = true;
    };
  }, [step, targetKind, key, enabled, refreshKey]);
  return map;
}

/** The compact counter chip on its own — used on the list rows, where a full
    note would be too heavy. Renders nothing when the counter would say
    nothing (T1's approvalLines decides that, not this component). */
export function ApprovalMeter({
  status,
  pending,
}: {
  status?: ApprovalStatus | null;
  pending: boolean;
}) {
  const lines = approvalLines(status, null, pending);
  if (!lines || !lines.meter) return null;
  return (
    <span className={'tma-appr-meter' + (lines.met ? ' met' : '')}>
      {lines.meter}
    </span>
  );
}

/** The full approval block that sits next to an approve action: the counter,
    every recorded vote with its time, the viewer's own «You approved» state
    and — while still pending and this viewer is excluded — who is expected.
    Every string comes from T1's approvalLines; a null means «render nothing»,
    which is the no-policy default. */
export function ApprovalNote({
  status,
  userId,
  pending,
  className,
}: {
  status?: ApprovalStatus | null;
  userId?: string | null;
  /** Is this step still AWAITING approval? Only then may the note say who it
      is waiting for — an approved document must never read as pending. */
  pending: boolean;
  className?: string;
}) {
  const lines = approvalLines(status, userId, pending);
  if (!lines) return null;
  const votes = status?.approvals || [];

  return (
    <div className={'tma-appr' + (className ? ' ' + className : '')}>
      {lines.meter || lines.mine ? (
        <div className="tma-appr-head">
          {lines.meter ? (
            <span className={'tma-appr-meter' + (lines.met ? ' met' : '')}>
              {lines.meter}
            </span>
          ) : null}
          {lines.mine ? <span className="tma-appr-mine">{lines.mine}</span> : null}
        </div>
      ) : null}
      {votes.length ? (
        <ul className="tma-appr-list">
          {votes.map((v, i) => (
            <li key={v.user_id || i}>
              <span className="tma-appr-who">{v.user_name || 'Approved'}</span>
              {fmtDate(v.approved_at) ? (
                <span className="tma-appr-when">{fmtDate(v.approved_at)}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {lines.waiting ? <div className="tma-appr-wait">{lines.waiting}</div> : null}
    </div>
  );
}

/* ═══════════════ AI chat sheet ═══════════════════════════════ */

export type DocChatMode = 'edit' | 'ask';

/** One rendered turn. `meta` is the small footnote under an assistant bubble
    (token / cost telemetry, or the status line of an edit). */
export interface DocChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'error';
  mode?: DocChatMode;
  text: string;
  meta?: string | null;
  /** For an error bubble: what to resend when «Try again» is tapped. */
  retry?: { text: string; mode: DocChatMode } | null;
}

let chatSeq = 0;
export function nextChatId(): string {
  chatSeq += 1;
  return 'c' + Date.now().toString(36) + chatSeq;
}

/** Token / cost footnote — the same numbers the web panel prints, compacted
    for a phone. Returns null when there is nothing worth showing. */
export function telemetryLine(t: unknown): string | null {
  if (!t || typeof t !== 'object') return null;
  const x = t as Record<string, number | string | undefined>;
  const bits: string[] = [];
  const inTok = Number(x.inputTokens || 0) + Number(x.cacheReadTokens || 0);
  if (inTok > 0) bits.push(fmtK(inTok) + ' in');
  if (Number(x.outputTokens || 0) > 0) bits.push(fmtK(Number(x.outputTokens)) + ' out');
  if (typeof x.costUsd === 'number' && x.costUsd > 0) {
    bits.push('$' + x.costUsd.toFixed(x.costUsd < 0.01 ? 4 : 2));
  }
  if (typeof x.durationMs === 'number' && x.durationMs > 0) {
    bits.push(Math.round(x.durationMs / 100) / 10 + 's');
  }
  return bits.length ? bits.join(' · ') : null;
}

export interface ChatSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Offer the «Ask» mode (question-only turns). Estimate: yes. Proposal:
      the backend has no ask route, so the toggle is not rendered. */
  askEnabled?: boolean;
  /** Approved estimates keep Ask while hiding all AI edit actions. */
  editEnabled?: boolean;
  models: { value: string; label: string }[];
  model: string;
  onModelChange: (value: string) => void;
  messages: DocChatMessage[];
  /** A turn is in flight — the composer locks and a typing bubble shows. */
  busy: boolean;
  /** The persisted thread is still loading (estimate only). */
  historyLoading?: boolean;
  onSend: (text: string, mode: DocChatMode) => void;
  /** A consequential edit can ask first. Cancelling keeps the composer intact. */
  beforeSend?: (text: string, mode: DocChatMode) => Promise<boolean>;
  /** Clears the persisted thread; omitted ⇒ no «Clear» action. */
  onClear?: () => void;
  onDraftStateChange?: (state: { dirty: boolean; busy: boolean }) => void;
  editPrompts?: string[];
  askPrompts?: string[];
}

export function ChatSheet({
  open,
  onClose,
  title = 'Lato AI',
  askEnabled = false,
  editEnabled = true,
  models,
  model,
  onModelChange,
  messages,
  busy,
  historyLoading,
  onSend,
  beforeSend,
  onClear,
  onDraftStateChange,
  editPrompts = [],
  askPrompts = [],
}: ChatSheetProps) {
  const [input, setInput] = useState('');
  const [mode, setMode] = useState<DocChatMode>('edit');
  const [preparing, setPreparing] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const pendingSend = useRef(false);
  const draftChangeRef = useRef(onDraftStateChange);
  draftChangeRef.current = onDraftStateChange;
  const listRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const stick = useRef(true);

  // An account that cannot ask is always in edit mode.
  const activeMode: DocChatMode = !editEnabled ? 'ask' : askEnabled ? mode : 'edit';
  useEffect(() => { draftChangeRef.current?.({ dirty: !!input.trim(), busy: busy || preparing }); }, [input, busy, preparing]);
  // A document-version switch remounts this composer. Clear the old viewer's
  // guard before the new one reports its own draft state.
  useEffect(() => () => { draftChangeRef.current?.({ dirty: false, busy: false }); }, []);

  // Stick to the newest turn unless the user scrolled up to read.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [messages, busy, open]);

  const onScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  }, []);

  useLayoutEffect(() => {
    if (taRef.current) autoGrow(taRef.current);
  }, [input, open]);

  const send = useCallback(
    async (text: string, m: DocChatMode) => {
      const t = text.trim();
      if (!t || busy || pendingSend.current || (!editEnabled && m === 'edit')) return;
      if (beforeSend) {
        pendingSend.current = true;
        setPreparing(true);
        setSendError(null);
        try {
          if (!await beforeSend(t, m)) return;
          setInput(current => current.trim() === t ? '' : current);
        } catch (cause) {
          setSendError((cause as Error).message || 'Could not confirm this edit. Your request is still here.');
          return;
        } finally {
          pendingSend.current = false;
          setPreparing(false);
        }
      } else {
        setInput('');
      }
      stick.current = true;
      haptic.impact('light');
      onSend(t, m);
    },
    [beforeSend, busy, editEnabled, onSend],
  );

  const prompts = activeMode === 'ask' ? askPrompts : editPrompts;
  const canSend = input.trim().length > 0 && !busy && !preparing;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      className="tma-doc-sheet-chat"
      headerAction={
        <div className="tma-doc-chat-head-actions">
          {onClear && messages.length ? (
            <button type="button" className="tma-sheet-text-btn" disabled={busy || preparing} onClick={onClear}>Clear</button>
          ) : null}
          <button type="button" className="tma-doc-chat-close" aria-label="Close Lato AI" onClick={onClose}><X size={19} /></button>
        </div>
      }
      footer={
        <div className="tma-doc-chat-composer">
          <div className="tma-doc-chat-controls">
            {askEnabled && editEnabled ? (
              <div className="tma-doc-seg" role="radiogroup" aria-label="Mode">
                {(
                  [
                    { value: 'edit', label: 'Edit' },
                    { value: 'ask', label: 'Ask' },
                  ] as { value: DocChatMode; label: string }[]
                ).map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    role="radio"
                    aria-checked={activeMode === m.value}
                    className={
                      'tma-doc-seg-opt' + (activeMode === m.value ? ' active' : '')
                    }
                    disabled={busy || preparing}
                    onClick={() => {
                      haptic.select();
                      setMode(m.value);
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            ) : (
              <span className="tma-doc-chat-mode-static">{editEnabled ? 'Edit document' : 'Ask about this estimate'}</span>
            )}
            {models.length > 1 ? <div className="tma-doc-seg" role="radiogroup" aria-label="Model">
              {models.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  role="radio"
                  aria-checked={model === m.value}
                  className={'tma-doc-seg-opt' + (model === m.value ? ' active' : '')}
                  disabled={busy || preparing}
                  onClick={() => {
                    haptic.select();
                    onModelChange(m.value);
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div> : null}
          </div>
          <div className="tma-doc-chat-row">
            <textarea
              ref={taRef}
              rows={1}
              className="tma-doc-chat-input"
              placeholder={
                activeMode === 'ask'
                  ? 'Ask about this document…'
                  : 'Describe the change you want…'
              }
              value={input}
              disabled={busy || preparing}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(input, activeMode);
                }
              }}
            />
            <button
              type="button"
              className="tma-doc-chat-send"
              aria-label="Send"
              disabled={!canSend}
              onClick={() => send(input, activeMode)}
            >
              ↑
            </button>
          </div>
          {sendError ? <div className="tma-banner err" role="alert">{sendError}</div> : null}
          <div className="tma-doc-chat-foot-hint">
            {activeMode === 'ask'
              ? 'Questions only — the document is not changed.'
              : 'The document is re-rendered and goes back to review.'}
          </div>
        </div>
      }
    >
      <div className="tma-doc-chat-list" ref={listRef} onScroll={onScroll}>
        {historyLoading ? (
          <div className="tma-doc-chat-sys">Loading the conversation…</div>
        ) : null}

        {!historyLoading && !messages.length ? (
          <div className="tma-doc-chat-empty">
            <span className="tma-lato-orb" aria-hidden="true" />
            <div className="tma-doc-chat-empty-title">
              {activeMode === 'ask' ? 'A clearer answer starts here.' : 'A clearer workflow starts here.'}
            </div>
            <div className="tma-doc-chat-empty-sub">
              {activeMode === 'ask'
                ? 'The answer comes back as text — nothing in the document moves.'
                : 'Describe the change; the document is rewritten and re-rendered.'}
            </div>
            {prompts.length ? (
              <div className="tma-doc-chat-chips">
                {prompts.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className="tma-doc-chat-chip"
                    disabled={busy || preparing}
                    onClick={() => send(p, activeMode)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {messages.map((m) => {
          if (m.role === 'system') {
            return (
              <div className="tma-doc-chat-sys" key={m.id}>
                {m.text}
              </div>
            );
          }
          if (m.role === 'error') {
            return (
              <div className="tma-doc-chat-bubble err" key={m.id}>
                <div className="tma-doc-chat-text">{m.text}</div>
                {m.retry && (editEnabled || m.retry.mode === 'ask') ? (
                  <button
                    type="button"
                    className="tma-doc-chat-retry"
                    disabled={busy || preparing}
                    onClick={() => send(m.retry!.text, m.retry!.mode)}
                  >
                    Try again
                  </button>
                ) : null}
              </div>
            );
          }
          return (
            <div
              className={'tma-doc-chat-bubble ' + m.role}
              key={m.id}
            >
              {m.role === 'assistant' && m.mode ? (
                <span className="tma-doc-chat-tag">
                  {m.mode === 'ask' ? 'Answer' : 'Edit'}
                </span>
              ) : null}
              <div className="tma-doc-chat-text">{m.text}</div>
              {m.meta ? <div className="tma-doc-chat-meta">{m.meta}</div> : null}
            </div>
          );
        })}

        {busy ? (
          <div className="tma-doc-chat-bubble assistant typing" aria-live="polite">
            <span className="tma-doc-chat-dot" />
            <span className="tma-doc-chat-dot" />
            <span className="tma-doc-chat-dot" />
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}

export default ChatSheet;
